import { useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../auth';

export type MatchEventType = 'STARTED' | 'UPDATED' | 'ENDED' | 'DELETED';

export type MatchEventMessage =
  | { kind: 'match'; matchId: string; type: MatchEventType }
  | { kind: 'reconnect' };

type Listener = (message: MatchEventMessage) => void;

const INITIAL_RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 60000;

const listeners = new Set<Listener>();
let eventSource: EventSource | null = null;
let sawError = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;

const isMatchEventType = (value: unknown): value is MatchEventType =>
  value === 'STARTED' || value === 'UPDATED' || value === 'ENDED' || value === 'DELETED';

const notify = (message: MatchEventMessage) => {
  listeners.forEach(listener => listener(message));
};

const handleMessage = (event: MessageEvent<string>) => {
  try {
    const payload = JSON.parse(event.data) as { matchId?: unknown; type?: unknown };
    if (typeof payload.matchId === 'string' && isMatchEventType(payload.type)) {
      notify({ kind: 'match', matchId: payload.matchId, type: payload.type });
    }
  } catch {
    // некорректный payload — молча игнорируем, keep-alive комментарии сюда не попадают
  }
};

const handleOpen = () => {
  reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
  if (sawError) {
    sawError = false;
    // реконнект после обрыва — могли пропустить события, подписчики тихо перезапрашивают данные
    notify({ kind: 'reconnect' });
  }
};

const handleError = () => {
  sawError = true;
  // readyState CLOSED = браузер сам не переподключится (502/503 при деплое, лимиты) — берём на себя
  if (eventSource?.readyState === EventSource.CLOSED) {
    detachEventSource();
    scheduleReconnect();
  }
};

const attachEventSource = () => {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/matches/events`;
  eventSource = new EventSource(url, { withCredentials: true });
  eventSource.addEventListener('match', handleMessage);
  eventSource.addEventListener('open', handleOpen);
  eventSource.addEventListener('error', handleError);
};

const detachEventSource = () => {
  if (!eventSource) return;
  eventSource.removeEventListener('match', handleMessage);
  eventSource.removeEventListener('open', handleOpen);
  eventSource.removeEventListener('error', handleError);
  eventSource.close();
  eventSource = null;
};

const clearReconnectTimer = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const scheduleReconnect = () => {
  if (reconnectTimer || listeners.size === 0) return;
  const delay = reconnectDelayMs;
  const jitter = Math.random() * 0.3 * delay;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (listeners.size > 0) attachEventSource();
  }, delay + jitter);
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
};

const openEventSource = () => {
  if (eventSource) return;
  clearReconnectTimer();
  attachEventSource();
};

const closeEventSource = () => {
  clearReconnectTimer();
  detachEventSource();
  sawError = false;
  reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
};

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  openEventSource();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      closeEventSource();
    }
  };
};

/** Подписка на общий SSE-поток `/api/matches/events` (одно соединение на вкладку). */
export const useMatchEvents = (handler: (message: MatchEventMessage) => void): void => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener: Listener = message => handlerRef.current(message);
    return subscribe(listener);
  }, []);
};
